import eventbus from 'tiny-emitter/instance';
import { TEXT, TYPE } from '@/constants';
import { DraggableMixin } from '@/core/mixins/DraggableMixin';
import { RotatableMixin } from '@/core/mixins/RotatableMixin';
import { SelectableMixin } from '@/core/mixins/SelectableMixin';

import Model from './model';
import { enhanceObj } from './enhanceObj';

export class Text extends RotatableMixin(DraggableMixin(SelectableMixin(Model))) {
  constructor(params) {
    super(params);
    this.initView();
  }

  get type() {
    return TYPE.TEXT;
  }

  changeFontSize(size) {
    this.setAttribute('size', size);
    this.label.font({ size });
    this.updateShape();
    eventbus.emit('selection:delayedHistory', 'Text size change');
  }

  changeFontColor(fill) {
    this.setAttribute('fill', fill);
    this.label.style({ fill });
    this.updateShape();
    eventbus.emit('selection:delayedHistory', 'Text color change');
  }

  changeText(label) {
    this.setAttribute('label', label);
    this.label.plain(label);
    this.updateShape();
    eventbus.emit('selection:delayedHistory', 'Text change');
  }

  initView() {
    const { position, label, size, fill, transform } = this.attributes;
    const [x, y] = position;
    this.label = this.canvas.plain(label).attr({
      x,
      y,
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      'font-style': 'normal',
    });
    this.label.font({ size, family: 'Roboto', style: 'normal' }).style({ fill });
    this.shape = this.canvas.rect(0, 0).attr({
      'fill-opacity': 0,
    });
    this.view = this.canvas.group().addClass('text');
    this.view.add(this.label).add(this.shape);
    this.view.transform(transform);
    this.updateShape();
  }

  updateShape() {
    this.label.rebuild(true);
    const { width, height } = this.rbox();
    const { x, y, height: h, width: w } = this.label.node.getBBox();
    const cx = x + w / 2;
    const cy = y + h / 2;
    this.shape.size(width, height);
    this.shape.center(cx, cy);
  }

  rbox(parent = this.svg) {
    return this.label.rbox(parent);
  }

  destroy() {
    this.view.remove();
    eventbus.emit('unit:removed', this);
  }

  clone(options) {
    const { keepPosition } = options;
    const textJson = this.toJSON();
    if (!keepPosition) {
      textJson.attributes.transform.e += 50;
      textJson.attributes.transform.f += 50;
    }
    textJson.uuid = this.getUniqueId();
    return Text.fromJSON(textJson);
  }

  toJSON() {
    const { type, uuid, sid } = this;
    const { x, y } = this.label.attr();
    const transform = this.view.transform('matrix');
    const attributes = Object.assign({}, this.attributes, {
      position: [x, y],
      transform,
    });
    return Text.filteredJSON({
      uuid,
      sid,
      type,
      attributes,
    });
  }

  static fromJSON(params) {
    return Text.create(params);
  }

  static create(params) {
    const attributes = {
      label: 'Text',
      size: TEXT.SIZE.MEDIUM,
      fill: TEXT.DEFAULTCOLOR,
      position: [0, 0],
      transform: '',
    };
    params.attributes = Object.assign({}, attributes, params.attributes);
    return new Text(params);
  }
}
